import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth/AuthProvider';
import { createApiClient } from '../lib/apiClient';
import { requestPasswordLink } from '../lib/auth/authApi';
import './auth.css';

interface FromState { from?: { pathname?: string; search?: string; hash?: string } }

export default function SignInScreen() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const fromLocation = (location.state as FromState | null)?.from;
  const from = fromLocation
    ? `${fromLocation.pathname ?? ''}${fromLocation.search ?? ''}${fromLocation.hash ?? ''}` || '/'
    : '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [linkSent, setLinkSent] = useState(false);

  // Asking for a password link happens before there is a session to attach.
  const bare = createApiClient({ tokens: { get: () => null, set: () => {} } });

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await auth.signInWithPassword(email.trim().toLowerCase(), password);
      navigate(from, { replace: true });
    } catch (err) {
      // Repeated verbatim: the server deliberately says the same thing for a
      // wrong password and for no account, and a friendlier rewording here
      // would give away the difference it is protecting.
      setError(err instanceof Error ? err.message : 'Sign-in failed.');
    } finally { setBusy(false); }
  }

  async function onPasskey() {
    setBusy(true); setError('');
    try {
      await auth.signInWithPasskey(email.trim().toLowerCase());
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No passkey was found on this device.');
    } finally { setBusy(false); }
  }

  async function onRequestLink() {
    setBusy(true); setError('');
    try {
      await requestPasswordLink(bare, email.trim().toLowerCase());
    } catch {
      // Ignored on purpose: the server answers the same either way, and an
      // error here would reveal what it refuses to.
    } finally { setLinkSent(true); setBusy(false); }
  }

  return (
    <section className="auth">
      <div className="auth__card">
        <p className="auth__kicker">Legacy Architect</p>
        <h1 className="auth__title">Welcome back.</h1>
        <p className="auth__sub">Sign in from any device with your email and password.</p>
        <p className="auth__alert" role="alert">{error}</p>
        <form onSubmit={onSubmit}>
          <label className="auth__field">
            <span className="auth__label">Email</span>
            <input className="auth__input" type="email" autoComplete="username webauthn"
                   value={email} onChange={(e) => setEmail(e.target.value)} required />
          </label>
          <label className="auth__field">
            <span className="auth__label">Password</span>
            <input className="auth__input" type="password" autoComplete="current-password"
                   value={password} onChange={(e) => setPassword(e.target.value)} required />
          </label>
          <div className="la-cta-wrap">
            <button className="la-btn-primary" type="submit" disabled={busy}>
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </div>
        </form>
        {/* The passkey is the shortcut on a device that already has one, not
            the way in. Most people arrive here on a machine that has never
            seen them. */}
        <button type="button" className="auth__linkBtn" disabled={busy}
                onClick={() => void onPasskey()}>
          Use a passkey on this device
        </button>
        <button type="button" className="auth__linkBtn" disabled={busy || email.trim() === ''}
                onClick={() => void onRequestLink()}>
          Set or reset your password
        </button>
        {linkSent && (
          <p className="auth__alt">
            If that address has an account, a link is on its way. It works once and expires in
            an hour.
          </p>
        )}
        <p className="auth__alt">Have an invite? <Link to="/welcome">Start here</Link></p>
        {/* The only way back for someone whose passkey did not travel to their
            new phone. It has to be on this screen: that is where they will be
            standing when they discover the problem. */}
        <p className="auth__alt">Lost your phone? <Link to="/recover">Get back in</Link></p>
      </div>
    </section>
  );
}
